import { _notNil } from '@/littledash';
import ReactPortal from '@/support/HOCs/ReactPortal';
import type { CSSProperties, FC, PropsWithChildren, Ref } from 'react';
import { memo, useMemo, useRef, useState } from 'react';
import { EMPTY_RECT, getMenuPosition } from '../Dropdown/Dropdown.utils';
import type { TooltipBubbleProps, TooltipElementProps, TooltipProps } from './Tooltip.model';
import styles from './Tooltip.module.scss';

const MEASURING_STYLES: CSSProperties = {
  position: 'absolute',
  zIndex: '9999',
  top: 0,
  left: 0,
  visibility: 'hidden',
};

const ARROW_WIDTH = 24;
const ARROW_EDGE_INSET = 4;
const VIEWPORT_MARGIN = 8;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(value, max));

const Tooltip: FC<TooltipProps> = ({ children, render }) => {
  const elementWrapperRef = useRef<HTMLDivElement>(null);
  const [tooltipActive, setTooltipActive] = useState<boolean>(false);
  const [bubbleRef, setBubbleRef] = useState<HTMLDivElement | null>(null);

  const bubblePosition = useMemo(() => {
    const toggleRect = elementWrapperRef?.current?.getBoundingClientRect() ?? EMPTY_RECT;
    const bubbleRect = bubbleRef?.getBoundingClientRect() ?? EMPTY_RECT;
    const { styles, arrowOrientation } = getMenuPosition({
      toggleElementDOMRect: toggleRect,
      window,
      dropdownElementDOMRect: bubbleRect,
    });

    const toggleCentre = toggleRect.left + toggleRect.width / 2;
    const left = clamp(
      toggleCentre - bubbleRect.width / 2,
      VIEWPORT_MARGIN,
      window.innerWidth - bubbleRect.width - VIEWPORT_MARGIN
    );
    const arrowLeft = clamp(
      toggleCentre - left - ARROW_WIDTH / 2,
      ARROW_EDGE_INSET,
      bubbleRect.width - ARROW_WIDTH - ARROW_EDGE_INSET
    );

    return { styles: { ...styles, left }, arrowAlign: arrowOrientation.y, arrowLeft };
  }, [elementWrapperRef, bubbleRef, window]);

  return (
    <>
      {_notNil(render) ? (
        <span
          data-testid="tooltip-wrapper"
          onMouseEnter={() => setTooltipActive(true)}
          onMouseLeave={() => setTooltipActive(false)}
        >
          <TooltipElement ref={elementWrapperRef}>{children}</TooltipElement>
          {tooltipActive && (
            <ReactPortal wrapperId="tooltip-portal-wrapper">
              <TooltipBubble ref={setBubbleRef} style={_notNil(bubbleRef) ? bubblePosition.styles : MEASURING_STYLES}>
                <div data-arrow-align={bubblePosition.arrowAlign} style={{ left: bubblePosition.arrowLeft }} />
                <div>{render}</div>
              </TooltipBubble>
            </ReactPortal>
          )}
        </span>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

const TooltipBubble = ({
  ref,
  style,
  className = '',
  children,
}: PropsWithChildren<TooltipBubbleProps> & { ref?: Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    style={{ ...style }}
    data-testid="tooltip-bubble"
    className={`${styles['tooltip-bubble']} ${className}`}
  >
    {children}
  </div>
);

const TooltipElement = memo(({ ref, children }: TooltipElementProps & { ref?: Ref<HTMLSpanElement> }) => (
  <span ref={ref} data-testid="tooltip-element-wrapper">
    {children}
  </span>
));

export default Tooltip;
